import { useState, useCallback, useEffect } from "react";
import classNames from "classnames";

import AscTextInput from "../../../AscInputFields/AscTextInput/AscTextInput";
import AscParagraph from "../../../AscTypography/AscParagraph/AscParagraph";
import AscFilesUploader from "../../../AscFilesUploader/AscFilesUploader";
import AscLinkInput from "../../../AscInputFields/AscLinkInput/AscLinkInput";
import AscFeedbackPreviewField from "../AscFeedbackPreviewField/AscFeedbackPreviewField";
import AscButton from "../../../AscButton/AscButton";

import styles from "./AscAddYoutubeVideos.module.scss";

function AscVideo({
  formik,
  currentIndex,
}: {
  formik: any;
  currentIndex: number;
}) {
  const handleUpload = useCallback((acceptedFiles: File[]) => {
    formik.setFieldValue(`youtube[${currentIndex}].image`, acceptedFiles[0]);
  }, []);

  const handleRemove = useCallback(() => {
    formik.setFieldValue(`youtube[${currentIndex}].image`, null);
  }, []);

  return (
    <div className={styles.form}>
      <div className={styles.formField}>
        <label className={styles.fieldLabel} htmlFor="name">
          Title
        </label>
        <AscTextInput
          className={classNames(styles.inputField)}
          fullWidth={true}
          placeholder="Enter name"
          id="name"
          name={`youtube[${currentIndex}].name`}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.youtube[currentIndex]?.name}
        />
      </div>

      <div className={styles.formField}>
        <label className={styles.fieldLabel} htmlFor="channel">
          Channel name
        </label>
        <AscTextInput
          className={classNames(styles.inputField)}
          fullWidth={true}
          placeholder="Enter channel name"
          id="channel"
          name={`youtube[${currentIndex}].channel`}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.youtube[currentIndex]?.channel}
        />
      </div>

      <div className={styles.formField}>
        <label className={styles.fieldLabel} htmlFor="link">
          Link
        </label>
        <AscLinkInput
          className={classNames(styles.inputField)}
          fullWidth={true}
          placeholder="Link to the video"
          id="link"
          name={`youtube[${currentIndex}].link`}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.youtube[currentIndex]?.link}
        />
      </div>

      <div className={styles.formField}>
        <AscParagraph className={styles.title}>Thumbnail</AscParagraph>
        <AscFilesUploader
          name={`youtube[${currentIndex}].image`}
          type="image"
          accept={{
            "image/*": [".jpeg", ".png"],
          }}
          multiple={false}
          onDrop={handleUpload}
          onRemoveFile={handleRemove}
          srcFile={
            typeof formik.values.youtube?.[currentIndex]?.image === "string" &&
            formik.values.youtube?.[currentIndex]?.image !== ""
              ? formik.values.youtube?.[currentIndex]?.image
              : undefined
          }
          initialValue={
            formik.values.youtube?.[currentIndex]?.image &&
            formik.values.youtube?.[currentIndex]?.image !== "" &&
            typeof formik.values.youtube?.[currentIndex]?.image !== "string"
              ? [formik.values.youtube?.[currentIndex]?.image]
              : undefined
          }
        />
        {formik.errors.youtube?.[currentIndex]?.image && (
          <div className={styles.fieldError}>
            {formik.errors.youtube?.[currentIndex]?.image}
          </div>
        )}
      </div>
    </div>
  );
}

export interface AscAddYoutubeVideosProps {
  className?: string;
  formik?: any;
  deleteFeedbackYoutube?: (youtubeId: string) => Promise<void>;
}

export default function AscAddYoutubeVideos({
  className,
  formik,
  deleteFeedbackYoutube,
}: AscAddYoutubeVideosProps) {
  const objInit = {
    name: "",
    channel: "",
    link: "",
    image: "",
    create: true,
  };

  const [currentIndex, setCurrentIndex] = useState(0);
  const [youtubeLength, setYoutubeLength] = useState(
    formik.values.youtube.length
  );

  const onDelete = (youtubeIndex: number, youtubeId?: string) => {
    if (youtubeId && deleteFeedbackYoutube) {
      deleteFeedbackYoutube(youtubeId);
    }
    formik.setFieldValue(
      "youtube",
      formik.values.youtube.filter(
        (youtube: any, index: number) => index !== youtubeIndex && youtube
      )
    );

    if (youtubeLength > 0) {
      setYoutubeLength((prev: number) => prev - 1);
      setCurrentIndex(0);
    }
  };

  useEffect(() => {
    if (youtubeLength === 0) {
      formik.setFieldValue(`youtube[0]`, objInit);
      setCurrentIndex(0);
      setYoutubeLength(1);
    }
  }, [youtubeLength]);

  return (
    <div className={classNames(styles.ground, className)}>
      <AscVideo
        formik={formik}
        currentIndex={currentIndex}
        key={currentIndex}
      />
      <div className={styles.previewSection}>
        {formik.values.youtube?.map((item: any, index: number) => {
          return (
            item && (
              <AscFeedbackPreviewField
                title={item?.name || "Start typing name"}
                key={item?.name}
                onEditClick={() => setCurrentIndex(index)}
                onDeleteClick={() => onDelete(index, item?.id)}
                selected={currentIndex === index}
                hasError={
                  formik.errors?.youtube &&
                  formik.errors?.youtube?.length > 0 &&
                  formik.errors?.youtube[index]
                }
              />
            )
          );
        })}

        <AscButton
          className={styles.button}
          variant="secondary"
          onClick={() => {
            setYoutubeLength((prev: number) => prev + 1);
            setCurrentIndex(youtubeLength);
            formik.setFieldValue(`youtube[${youtubeLength}]`, objInit);
          }}
        >
          + Add video
        </AscButton>
      </div>
    </div>
  );
}
